<template>
  <div>
    <div class="mb-2">
      <h2 class="text-[20px] font-semibold overflow-hidden text-ellipsis">
        Checkout Details
      </h2>
    </div>
    <p
      class="secondary_color text-[14px] font-normal leading-[17px] overflow-hidden text-ellipsis mb-10"
    >
      Review mailboxes before checkout.
    </p>

    <div class="mb-10">
      <div class="flex flex-col gap-2 mb-4">
        <div class="text_color text-[17px] font-semibold leading-[17px]">
          Mailboxes to Purchase
        </div>

      </div>

      <div class="max-h-[240px] overflow-y-auto mb-8">
        <div class="flex flex-col gap-2">
          <div
            v-for="(email, index) in mailboxEmails"
            :key="index"
            class="text-[14px] font-normal"
          >
            {{ email }}
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2 mb-4">
        <div class="text_color text-[16px] font-semibold leading-[17px] mb-2">
          Mailbox Cost
        </div>
        <div>
          <span class="text_color text-[24px] font-bold leading-[28px] mr-1">{{ creditsPerMailbox.toLocaleString() }} credits</span>
          <span class="secondary_color text-[14px] font-normal leading-[17px]">/mailbox /year</span>
        </div>
      </div>
    </div>

    <div class="border_bottom my-10"></div>

    <div class="flex flex-col">
      <h3 class="text_color text-[16px] font-semibold leading-[19px] mb-4">
        Total Billing Credits
      </h3>
      <div class="text_color text-[28px] font-bold mb-2">
        <span class="text-[32px] font-bold">{{ formattedTotalCredits }} credits</span>
        <span class="secondary_color text-[14px] font-normal leading-[17px]">
          for 1 year
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";

// Default value as fallback
const creditsPerMailbox = ref(2100);

const props = defineProps({
  formattedMailboxes: {
    type: Object,
    default: () => ({ domains: [] }),
  },
});

const emit = defineEmits(["update:checkoutData"]);

const checkoutData = ref({
  domains: [],
  mailboxes: [],
  totalCredits: 0,
});

// Fetch credit rates on component mount
const fetchCreditRates = async () => {
  try {
    const response = await DashboardService.getCreditRates();
    if (response.success && response.data) {
      creditsPerMailbox.value = response.data.mailbox_credits || 2100;
    }
  } catch (error) {
    console.error("Failed to fetch credit rates, using default:", error);
    // Keep default value if API call fails
  }
};

const mailboxCount = computed(() => {
  return checkoutData.value.mailboxes.length;
});

const totalCredits = computed(() => {
  return mailboxCount.value * creditsPerMailbox.value;
});

const formattedTotalCredits = computed(() => {
  return totalCredits.value.toLocaleString();
});

const mailboxEmails = computed(() => {
  return checkoutData.value.mailboxes.map((mailbox) => mailbox.email);
});

const processCheckoutData = () => {
  if (
    !props.formattedMailboxes ||
    !props.formattedMailboxes.domains ||
    !Array.isArray(props.formattedMailboxes.domains)
  ) {
    console.error("Invalid formattedMailboxes data:", props.formattedMailboxes);
    message.error("Invalid mailbox data for checkout");
    return;
  }

  const domains = props.formattedMailboxes.domains;
  const mailboxes = [];

  domains.forEach((domain) => {
    if (domain.mailboxes && Array.isArray(domain.mailboxes)) {
      mailboxes.push(...domain.mailboxes);
    }
  });

  const calculatedTotalCredits = mailboxes.length * creditsPerMailbox.value;

  checkoutData.value = {
    domains,
    mailboxes,
    totalCredits: calculatedTotalCredits,
  };

  emit("update:checkoutData", checkoutData.value);
};

onMounted(async () => {
  await fetchCreditRates();
  processCheckoutData();
});

watch(
  () => props.formattedMailboxes,
  () => {
    processCheckoutData();
  },
  { deep: true }
);

defineExpose({
  getCheckoutData: () => checkoutData.value,
});
</script>
